import React, { useMemo } from 'react';
import classNames from 'classnames';
import { AspectRatioWrapper, ResponsiveImage, IconHeart, IconPlay } from '../../../components';

import css from './ListingImageGallery.module.css';

const ListingImageGalleryDesktopGrid = props => {
  const {
    images,
    imageVariants,
    aspectWidth,
    aspectHeight,
    totalImages,
    onOpenFullscreenAtIndex,
    onOpenFullscreen,
    intl,
    currentUser,
    listing,
    onToggleFavorites,
    videoUrl,
    optimizedImages,
  } = props;

  const isFavorite = useMemo(
    () => currentUser?.attributes?.profile?.privateData?.favorites?.includes(listing?.id?.uuid),
    [currentUser, listing]
  );

  const showFavoriteButton = typeof onToggleFavorites === 'function';

  const desktopMediaItems = useMemo(() => {
    const imageItems = (images || []).map((image, i) => ({
      type: 'image',
      image,
      key: image.id?.uuid || `image-${i}`,
      fullIndex: i + (videoUrl ? 1 : 0),
      imageIndex: i,
    }));

    const withVideo = videoUrl
      ? [{ type: 'video', key: 'listing-video', videoUrl, image: images?.[0], fullIndex: 0 }, ...imageItems]
      : imageItems;

    return withVideo.slice(0, 4);
  }, [images, videoUrl]);
  const mainItem = desktopMediaItems[0];
  const sideItems = desktopMediaItems.slice(1);

  if (!mainItem) {
    return null;
  }

  return (
    <div className={css.desktopHeroGrid}>
      <div className={css.desktopHeroMain}>
        <AspectRatioWrapper
          width={4}
          height={3}
          className={classNames(css.itemWrapper, css.desktopHeroFrame, css.desktopHeroMainFrame)}
        >
          <div className={css.itemCentering} onClick={() => onOpenFullscreenAtIndex(mainItem.fullIndex)}>
            {showFavoriteButton ? (
              <button
                type="button"
                aria-pressed={isFavorite}
                className={classNames(css.mobileFavoriteButton, {
                  [css.favoritedIcon]: isFavorite,
                })}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleFavorites(isFavorite);
                }}
              >
                <IconHeart isFilled={isFavorite} />
              </button>
            ) : null}
            {mainItem.type === 'video' ? (
              <div className={css.videoThumbnail}>
                <ResponsiveImage
                  rootClassName={css.item}
                  image={mainItem.image}
                  alt={intl.formatMessage({ id: 'ListingImageGallery.videoThumbnailAltText' })}
                  variants={imageVariants}
                  sizes="100vw"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  optimizedImages={optimizedImages}
                  imageIndex={0}
                />
                <div className={css.videoThumbnailOverlay}>
                  <IconPlay />
                </div>
              </div>
            ) : (
              <ResponsiveImage
                rootClassName={css.item}
                image={mainItem.image}
                alt={intl.formatMessage(
                  { id: 'ListingImageGallery.imageAltTextDel' },
                  { index: mainItem.imageIndex + 1, count: totalImages }
                )}
                variants={imageVariants}
                sizes="100vw"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                optimizedImages={optimizedImages}
                imageIndex={mainItem.imageIndex}
                preferPreviewCrop
              />
            )}
            {totalImages > 0 ? (
              <button
                type="button"
                className={css.viewAll}
                onClick={e => {
                  e.stopPropagation();
                  onOpenFullscreen();
                }}
              >
                {intl.formatMessage(
                  { id: 'ListingImageGallery.viewImagesButton' },
                  { count: totalImages }
                )}
              </button>
            ) : null}
          </div>
        </AspectRatioWrapper>
      </div>
      {sideItems?.length ? (
        <div className={css.desktopHeroSide}>
          {sideItems.map(mediaItem => (
            <div
              key={mediaItem.key}
              className={classNames(css.desktopHeroSideItem, css.desktopHeroFrame)}
              onClick={() => onOpenFullscreenAtIndex(mediaItem.fullIndex)}
            >
              {mediaItem.type === 'video' ? (
                <div className={css.videoThumbnail}>
                  <ResponsiveImage
                    rootClassName={css.item}
                    image={mediaItem.image}
                    alt={intl.formatMessage({ id: 'ListingImageGallery.videoThumbnailAltText' })}
                    variants={imageVariants}
                    sizes="33vw"
                    optimizedImages={optimizedImages}
                    imageIndex={0}
                  />
                  <div className={css.videoThumbnailOverlay}>
                    <IconPlay />
                  </div>
                </div>
              ) : (
                <ResponsiveImage
                  rootClassName={css.item}
                  image={mediaItem.image}
                  alt={intl.formatMessage(
                    { id: 'ListingImageGallery.imageAltTextDel' },
                    { index: mediaItem.imageIndex + 1, count: totalImages }
                  )}
                  variants={imageVariants}
                  sizes="33vw"
                  optimizedImages={optimizedImages}
                  imageIndex={mediaItem.imageIndex}
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default ListingImageGalleryDesktopGrid;
